import { SelectFileStrategy } from '@mydocma/component-library/src/lib/drag-drop-file-upload';
import { MatDialog } from '@angular/material/dialog';
import { NativeService } from '@mydocma/core/src/lib/native';
import { AndroidFileChoiceDialogComponent } from './android-file-choice-dialog/android-file-choice-dialog.component';
import { firstValueFrom } from 'rxjs';

export class AndroidFileChoiceStrategy implements SelectFileStrategy {
  text = 'COMP-LIB.dragDropFileUpload.selectFileMobile.content_photo';
  icon = {
    name: 'photo_camera',
    outlined: false
  };
  showMobileButton = false; 

  constructor(private dialog: MatDialog, private nativeService: NativeService) {}

  private isAndroidBrowser(): boolean {
    return !!this.nativeService?.isWeb &&
      typeof navigator !== 'undefined' &&
      /Android/i.test(navigator.userAgent || '');
  }

  private openFileInput(options: {
    accept: string;
    multiple?: boolean;
    capture?: 'environment';
  }): Promise<File[]> {
    return new Promise<File[]>((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = options.accept;
      input.multiple = !!options.multiple;

      if (options.capture) {
        input.setAttribute('capture', options.capture);
      } else {
        input.removeAttribute('capture');
      }

      input.onchange = (event: Event) => {
        const files = Array.from((event.target as HTMLInputElement).files || []) as File[];
        resolve(files);
        input.remove();
      };

      input.oncancel = () => {
        resolve([]);
        input.remove();
      };

      input.click();
    });
  }

  async getFiles(acceptedTypes?: string[], multiple?: boolean): Promise<File[]> {
    const isCapacitorAndroid = !!this.nativeService?.isAndroid;
    const isAndroidWeb = this.isAndroidBrowser();
    const normalizedAccepted = (acceptedTypes && acceptedTypes.length)
      ? acceptedTypes
      : ['image/*'];

    // Nicht Android: normaler Dateipicker
    if (!isCapacitorAndroid && !isAndroidWeb) {
      return this.openFileInput({
        accept: normalizedAccepted.join(','),
        multiple: !!multiple
      });
    }

    const choice = await firstValueFrom(
      this.dialog.open(AndroidFileChoiceDialogComponent).afterClosed()
    );

    if (choice !== 'camera' && choice !== 'file') {
      return [];
    }

    if (choice === 'camera') {
      //Go Apps: Direct camera access via Capacitor
      if (isCapacitorAndroid) {
        const photo = await this.nativeService.takePhoto();
        return photo ? [photo] : [];
      }

      // Android browser: Using file input with capture for camera access
      return this.openFileInput({
        accept: 'image/*',
        multiple: false,
        capture: 'environment'
      });
    }

    // File (both Android cases): Using file input without capture
    return this.openFileInput({
      accept: normalizedAccepted.join(','),
      multiple: !!multiple
    });
  }
}
